import { Fragment, useState } from 'react';
import type { Deal, TabState } from '../../types';
import { buildSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
type DealViewMode = 'cards' | 'table' | 'tile' | 'gallery' | 'waterfall';

/* ─── SVG icons for the view switcher ──────────────────────────────────── */

function IconCards() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-205" data-mp="106j7apc1uwas">
      <rect x="1" y="1" width="6" height="6" rx="1" data-id="element-206" data-mp="3m0s57n72jjmh" />
      <rect x="9" y="1" width="6" height="6" rx="1" data-id="element-207" data-mp="3lzh0nl5su7ub" />
      <rect x="1" y="9" width="6" height="6" rx="1" data-id="element-208" data-mp="3h30egwi5u3jv" />
      <rect x="9" y="9" width="6" height="6" rx="1" data-id="element-209" data-mp="3h04ez3qnz6tb" />
    </svg>;
}
function IconTable() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-210" data-mp="3vsxw0tll3v03">
      <rect x="1" y="2" width="14" height="12" rx="1.5" data-id="element-211" data-mp="332fw21g5q9qw" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" data-id="element-212" data-mp="332zfjmlgh671" />
      <line x1="1" y1="9" x2="15" y2="9" data-id="element-213" data-mp="2y3csymjqmdlk" />
      <line x1="6" y1="5.5" x2="6" y2="14" data-id="element-214" data-mp="2y4o4x9nc6rnr" />
    </svg>;
}
function IconTile() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-215" data-mp="3c4743cy66snx">
      <rect x="1" y="1" width="4" height="4" rx="0.5" data-id="element-216" data-mp="38091gs9myzq4" />
      <rect x="6" y="1" width="4" height="4" rx="0.5" data-id="element-217" data-mp="rc56ikjvwsh4" />
      <rect x="11" y="1" width="4" height="4" rx="0.5" data-id="element-218" data-mp="rerf8niihe0x" />
      <rect x="1" y="6" width="4" height="4" rx="0.5" data-id="element-219" data-mp="na9zczf9vkvf" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" data-id="element-220" data-mp="3g5s7yt2unqke" />
      <rect x="11" y="6" width="4" height="4" rx="0.5" data-id="element-221" data-mp="3g5a35824g40v" />
      <rect x="1" y="11" width="4" height="4" rx="0.5" data-id="element-222" data-mp="3l4d9qwxr2iay" />
      <rect x="6" y="11" width="4" height="4" rx="0.5" data-id="element-223" data-mp="3l31xs9u5i48r" />
      <rect x="11" y="11" width="4" height="4" rx="0.5" data-id="element-224" data-mp="3q3pw7gkplnd0" />
    </svg>;
}
function IconGallery() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-225" data-mp="3u4jg7yv16i0m">
      <rect x="1" y="1" width="6" height="7" rx="1" data-id="element-226" data-mp="2xh5248cumx5r" />
      <rect x="9" y="1" width="6" height="7" rx="1" data-id="element-227" data-mp="32dltrcj9btnl" />
      <rect x="1" y="10" width="14" height="5" rx="1" data-id="element-228" data-mp="32cam31moy1r7" />
    </svg>;
}
function IconWaterfall() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-229" data-mp="3b6p256f1pgbd">
      <line x1="2" y1="1" x2="2" y2="15" data-id="element-230" data-mp="2epc94dxs5by1" />
      <line x1="6" y1="1" x2="6" y2="15" data-id="element-231" data-mp="2eokfh8nqtoae" />
      <line x1="10" y1="1" x2="10" y2="15" data-id="element-232" data-mp="2iq60xiecfaw7" />
      <line x1="14" y1="1" x2="14" y2="15" data-id="element-233" data-mp="2iouv7d1c7t2g" />
      <rect x="1" y="3" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-234" data-mp="2b6wspshkpi8q" />
      <rect x="5" y="6" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-235" data-mp="267shyxf03cga" />
      <rect x="9" y="2" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-236" data-mp="265z8zbgx0g54" />
      <rect x="13" y="8" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-237" data-mp="228bc42vnwdal" />
    </svg>;
}
const VIEW_OPTIONS: {
  mode: DealViewMode;
  label: string;
  icon: React.FC;
}[] = [{
  mode: 'cards',
  label: 'Cards',
  icon: IconCards
}, {
  mode: 'table',
  label: 'Table',
  icon: IconTable
}, {
  mode: 'tile',
  label: 'Tile',
  icon: IconTile
}, {
  mode: 'gallery',
  label: 'Gallery',
  icon: IconGallery
}, {
  mode: 'waterfall',
  label: 'Waterfall',
  icon: IconWaterfall
}];

/* ─── View switcher bar ────────────────────────────────────────────────── */

function ViewSwitcher({
  active,
  onChange
}: {
  active: DealViewMode;
  onChange: (m: DealViewMode) => void;
}) {
  return <div className="view-switcher" data-id="element-238" data-mp="14ksrnusn1mqr">
      {VIEW_OPTIONS.map(opt => <button key={opt.mode} className={active === opt.mode ? 'active' : ''} onClick={() => onChange(opt.mode)} title={opt.label} data-id="element-239" data-mp="34l7tl4pehrn1">
          <opt.icon data-id="element-240" data-mp="2utv2wgfc97il" />
          {opt.label}
        </button>)}
    </div>;
}

/* ─── Age bucket helper for waterfall ──────────────────────────────────── */

function ageBucket(age: number | null): string {
  if (age == null) return 'Unknown';
  if (age <= 30) return '0–30 days';
  if (age <= 60) return '31–60 days';
  if (age <= 90) return '61–90 days';
  return '90+ days';
}
const AGE_BUCKETS = ['0–30 days', '31–60 days', '61–90 days', '90+ days'];
function groupByAgeBucket(deals: Deal[]): Record<string, Deal[]> {
  const groups: Record<string, Deal[]> = {};
  for (const b of AGE_BUCKETS) groups[b] = [];
  for (const d of deals) {
    const b = ageBucket(d.age);
    if (!groups[b]) groups[b] = [];
    groups[b].push(d);
  }
  return groups;
}

/* ─── Individual view renderers ────────────────────────────────────────── */

function TableView({
  deals
}: {
  deals: Deal[];
}) {
  return <table className="deal-table" data-id="element-241" data-mp="1uo0xvajbqak6">
      <thead data-id="element-242" data-mp="1ukv95welxaoz">
        <tr data-id="element-243" data-mp="29bm4vfjbktxd">
          <th style={{
          width: '9%'
        }} data-id="element-244" data-mp="29c47ctwks4dq">ID</th>
          <th style={{
          width: '18%'
        }} data-id="element-245" data-mp="29d5lj78w16zo">Account</th>
          <th style={{
          width: '20%'
        }} data-id="element-246" data-mp="24kcegcpy5mkf">Request</th>
          <th style={{
          width: '8%'
        }} data-id="element-247" data-mp="24jksltm5j37c">Age</th>
          <th style={{
          width: '10%'
        }} data-id="element-248" data-mp="2ibr9xj11jg8d">GNARR</th>
          <th style={{
          width: '10%'
        }} data-id="element-249" data-mp="2iafu2kubmi0w">Close Q</th>
          <th style={{
          width: '13%'
        }} data-id="element-250" data-mp="2ddpedymfhog0">Offering</th>
          <th style={{
          width: '12%'
        }} data-id="element-251" data-mp="2dcxw1phudn7o">Sub Region</th>
        </tr>
      </thead>
      <tbody data-id="element-252" data-mp="2s95fuopc8mmf">
        {deals.map(d => <tr key={d.id} data-id="element-253" data-mp="2s83zc4pjz7xb">
            <td style={{
          color: '#8892a0',
          fontSize: 9
        }} data-id="element-254" data-mp="2nau6sg6vag59">{d.id}</td>
            <td title={d.account} data-id="element-255" data-mp="2n9j2mf9if6dm">{d.account.substring(0, 24)}</td>
            <td title={d.requestName} data-id="element-256" data-mp="xwz0retbplou">
              <a href={getURL(d)} target="_blank" rel="noopener noreferrer" style={{
            color: '#1d4ed8',
            textDecoration: 'none'
          }} data-id="element-257" data-mp="sxcfci3ccz4y">
                {d.requestName.substring(0, 30)}
              </a>
            </td>
            <td className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
        }} data-id="element-258" data-mp="1mmju3wfecrkp">
              {formatAge(d.age)}
            </td>
            <td data-id="element-259" data-mp="1wm2bc53x9z92">{fmtG(d.gnarr)}</td>
            <td data-id="element-260" data-mp="1wiwnet7153ex">{d.close_q}</td>
            <td data-id="element-261" data-mp="21kvry6eia6dg"><span className="badge b-blue" style={{
            fontSize: 8
          }} data-id="element-262" data-mp="21l5ipw7dkhk7">{d.offering.substring(0, 18)}</span></td>
            <td data-id="element-263" data-mp="25l5zhnhy54uf"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-264" data-mp="25kw4tmlyi9ie">{d.sub_region}</span></td>
          </tr>)}
      </tbody>
    </table>;
}
function TileView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-tile-grid" data-id="element-265" data-mp="2fafpmxt8cdfw">
      {deals.map(d => <div key={d.id} className="deal-tile" title={`${d.account}\n${d.requestName}\nAge: ${formatAge(d.age)}\nGNARR: ${fmtG(d.gnarr)}`} data-id="element-266" data-mp="2kb3m3z089mhi">
          <div className="dt-id" data-id="element-267" data-mp="2pagbaqekn5n9">{d.id}</div>
          <div className="dt-acct" data-id="element-268" data-mp="dml9525se3lj">{d.account.substring(0, 14)}</div>
          <span className={`dt-age ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-269" data-mp="dnwcj0vbl9c4">
            {formatAge(d.age)}
          </span>
        </div>)}
    </div>;
}
function GalleryView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-gallery" data-id="element-270" data-mp="3uw9l1xbftr51">
      {deals.map(d => <div key={d.id} className="deal-gallery-card" data-id="element-271" data-mp="wdm83q6wpz53">
          <div className="dg-id" data-id="element-272" data-mp="wf5k34lj5o12">{d.id}</div>
          <div className="dg-title" data-id="element-273" data-mp="rdpzter6fljq">{d.account}</div>
          <div style={{
        fontSize: 10,
        color: '#344054',
        marginBottom: 4
      }} data-id="element-274" data-mp="rg2qqhewx68x">
            <a href={getURL(d)} target="_blank" rel="noopener noreferrer" style={{
          color: '#1d4ed8',
          textDecoration: 'none'
        }} data-id="element-275" data-mp="mlor22t7wcyg">
              {d.requestName.substring(0, 40)}
            </a>
          </div>
          <div className="dg-meta" data-id="element-276" data-mp="1f4vf9st7gbti">
            <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-277" data-mp="1acu46a7rwr52">{formatAge(d.age)}</span>
            <span className="badge b-blue" data-id="element-278" data-mp="inzkr532a8m3">{fmtG(d.gnarr)}</span>
            <span className="badge b-purple" style={{
          fontSize: 8
        }} data-id="element-279" data-mp="ng9aak09l6nu">{d.close_q}</span>
            {isAged(d) && <span className="badge b-red" data-id="element-280" data-mp="9pnlyfs1k4ik">⚠ aged</span>}
          </div>
          <div className="dg-footer" data-id="element-281" data-mp="enfoe3qbb910">
            {d.sub_region} · {d.offering.substring(0, 26)}
          </div>
        </div>)}
    </div>;
}
function WaterfallView({
  deals
}: {
  deals: Deal[];
}) {
  const groups = groupByAgeBucket(deals);
  return <div className="deal-waterfall" data-id="element-282" data-mp="16e2a3dfld0h0">
      {AGE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-283" data-mp="xdx334eb843d">
            <div className="deal-waterfall-lane-header" data-id="element-284" data-mp="xcc9bgztkp6k">
              <span data-id="element-285" data-mp="3d51qa6dtuej8">{bucket}</span>
              <span className="lane-count" data-id="element-286" data-mp="3d3qf3li1y4i3">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-287" data-mp="3i57bp4ti74sv">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-288" data-mp="3ajtwgo3jfx0w">none</div>}
              {items.map(d => <div key={d.id} className="deal-waterfall-card" data-id="element-289" data-mp="3jltk3z925zct">
                  <div className="dw-id" data-id="element-290" data-mp="3emgvpa2jgk84">{d.id}</div>
                  <div className="dw-acct" title={d.account} data-id="element-291" data-mp="3enrz38s2npyp">{d.account.substring(0, 18)}</div>
                  <div className="dw-tags" data-id="element-292" data-mp="2qxp9r0fjky3s">
                    <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} style={{
                fontSize: 8
              }} data-id="element-293" data-mp="2qujnru27mcca">{formatAge(d.age)}</span>
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-294" data-mp="30ojz4do34dtg">{fmtG(d.gnarr)}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Dispatch view ────────────────────────────────────────────────────── */

function DealExpandedView({
  deals,
  mode
}: {
  deals: Deal[];
  mode: DealViewMode;
}) {
  if (deals.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '8px 0',
      fontStyle: 'italic'
    }} data-id="element-295" data-mp="4nmdllxtz4o2">No in-progress deals.</div>;
  }
  switch (mode) {
    case 'table':
      return <TableView deals={deals} data-id="element-296" data-mp="3vvqpl9khlbt7" />;
    case 'tile':
      return <TileView deals={deals} data-id="element-297" data-mp="3rr0v9p5h85ae" />;
    case 'gallery':
      return <GalleryView deals={deals} data-id="element-298" data-mp="3ruf0r69fsum2" />;
    case 'waterfall':
      return <WaterfallView deals={deals} data-id="element-299" data-mp="3mtr68am01vn3" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-300" data-mp="3i17tffv4z4jc">
          {deals.map(d => <div key={d.id} className="deal-chip" data-id="element-301" data-mp="3d2wkd7co0y26">
              <div className="dc-id" data-id="element-302" data-mp="3d3phdglto974">{d.id}</div>
              <div className="dc-name" data-id="element-303" data-mp="384cyt81zhoac">{d.account.substring(0, 35)}</div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-304" data-mp="3828jzv2jr4va"><a href={getURL(d)} target="_blank" rel="noopener noreferrer" data-id="element-305" data-mp="3416jlaww8mrq">{d.requestName.substring(0, 35)}</a></div>
              <div className="dc-tags" data-id="element-306" data-mp="2z46ofv1l14fe">
                <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-307" data-mp="1elmdqafe8z5w">{formatAge(d.age)}</span>
                <span className="badge b-blue" data-id="element-308" data-mp="1jmjv4kskxp6c">{fmtG(d.gnarr)}</span>
                <span className="badge b-purple" style={{
              fontSize: 8
            }} data-id="element-309" data-mp="1nnc55g99zy3o">{d.close_q}</span>
                {isAged(d) && <span className="badge b-red" data-id="element-310" data-mp="1sju9if0p1ufh">⚠ aged</span>}
              </div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-311" data-mp="vvnxh71fzzr6">
                {d.sub_region} · {d.offering.substring(0, 26)}
              </div>
            </div>)}
        </div>;
  }
}

/* ─── SC Matrix (unchanged) ───────────────────────────────────────────── */

function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    const bkts = bucketProducts(d.products || '');
    for (const scName of splitSCNames(d.sc)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + d.id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-312" data-mp="2fieka9v9in1k">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-313" data-mp="2fimws0nh3qc8">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-314" data-mp="386sj2ru6dne6">
        <tr data-id="element-315" data-mp="388de0ikej8ck">
          <th data-id="element-316" data-mp="388mvtivam976">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-317" data-mp="3d4bwxqmyny5l">{c}</th>)}
          <th data-id="element-318" data-mp="3d4ka7jmzx5hb">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-319" data-mp="2ygz89t1uxcaz">
        {scRows.map(row => <tr key={row.full} data-id="element-320" data-mp="2yg7bwg0frajn">
            <td title={row.full} data-id="element-321" data-mp="33fkatz4l082l">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-322" data-mp="33dhd0sx6tanb">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-323" data-mp="3r3jzmtic1oej">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-324" data-mp="3vwd5xltg94sq">—</span>}
              </td>)}
            <td data-id="element-325" data-mp="27ajusxugmc4z"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-326" data-mp="27ajwd2a3yk73">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-327" data-mp="2375fk9echzsl">
        <tr data-id="element-328" data-mp="23at938jsim7b">
          <td data-id="element-329" data-mp="1y9cajjos3btw">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-330" data-mp="1y9vqiuuvbq5a"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-331" data-mp="1y9m0j79tpizl">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-332" data-mp="1tcdir7sl7gyv"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-333" data-mp="1tcde2uhn6ssj">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}

/* ─── Main panel ───────────────────────────────────────────────────────── */

export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const [dealView, setDealView] = useState<DealViewMode>('cards');
  const [scSearch, setScSearch] = useState('');
  const {
    deals
  } = useDeals();
  const scDataAll = buildSCList(state.r, state.sub, state, deals);
  const scData = scSearch ? scDataAll.filter(s => s.n.toLowerCase().includes(scSearch.toLowerCase())) : scDataAll;
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(d => splitSCNames(d.sc))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allIP);
  const maxIP = Math.max(...scData.map(s => s.ip), 1);
  const maxClosed = Math.max(...scData.map(s => s.closed), 1);
  return <div data-id="element-334" data-mp="2jsdw9flh41zh">
      {/* Metrics */}
      <div className="mrow" data-id="element-335" data-mp="2jtoxb7njavmw">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-336" data-mp="2jsvntyx6xs12" />
        <MetricCard label="Requests / person" value={scData.length ? +(allIP.length / scData.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-337" data-mp="2erpvi27g06s2" />
        <MedianAgeCard ages={ages} data-id="element-338" data-mp="2nso12oxj4mcr" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-339" data-mp="2nsebv3kb6j84" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — deals (click row to expand)" data-id="element-340" data-mp="2xkajb58ablp0" />
      <div className="tw" data-id="element-341" data-mp="2xm4u3qpudxfd">
        <div style={{
        padding: '8px 12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-id="element-342" data-mp="3bjjnf6j7upcm">
          <input type="text" placeholder="Search SC name…" value={scSearch} onChange={e => {
          setScSearch(e.target.value);
          setExpandedSC(null);
        }} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          width: 160,
          color: '#344054',
          background: '#fff'
        }} data-id="element-343" data-mp="37hycvru5mn5l" />
          {expandedSC && <ViewSwitcher active={dealView} onChange={setDealView} data-id="element-344" data-mp="1712c829eb3dh" />}
        </div>
        <table className="tbl" data-id="element-345" data-mp="t9nr9pvxgs3s">
          <thead data-id="element-346" data-mp="t8conte7xqe9">
            <tr data-id="element-347" data-mp="y9jw1rcg8nke">
              <th style={{
              width: '26%'
            }} data-id="element-348" data-mp="y90bs3zbtn37">SC Name</th>
              <th style={{
              width: '18%'
            }} data-id="element-349" data-mp="y7fjym4eci91">In-progress</th>
              <th style={{
              width: '18%'
            }} data-id="element-350" data-mp="jb7zdkp2tet8">Closed</th>
              <th style={{
              width: '13%'
            }} data-id="element-351" data-mp="jem2iv5kds1q">Avg age</th>
              <th style={{
              width: '13%'
            }} data-id="element-352" data-mp="oc5hxye00av9">Median age</th>
              <th style={{
              width: '12%'
            }} data-id="element-353" data-mp="abunymhe6g5v">GNARR (In-Progress)</th>
            </tr>
          </thead>
          <tbody data-id="element-354" data-mp="faebusfjq20v">
            {scData.map((p, rowPosition) => <Fragment key={`${p.n}-${rowPosition}`} data-id="element-355" data-mp="fcs4yvqo145s" data-mpi={rowPosition}>
                <tr key={p.n} onClick={() => setExpandedSC(expandedSC === p.n ? null : p.n)} data-id="element-356" data-mp="24pccoctj9j2h">
                  <td data-id="element-357" data-mp="29oyukzkb3nhm">
                    <span className="name-link" data-id="element-358" data-mp="29ndvcwn1rgdf">
                      {p.n}
                      <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-359" data-mp="3i9e5iallsnvy">▾</span>
                    </span>
                  </td>
                  <td data-id="element-360" data-mp="3datacpl7kued">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-361" data-mp="38jbciaehf9xm">
                      <div className="pf" style={{
                    width: `${Math.round(p.ip / maxIP * 100)}%`
                  }} data-id="element-362" data-mp="38hrrk6hvsapi" />
                    </div>
                    <strong data-id="element-363" data-mp="34fwtdav8ydfy">{p.ip}</strong>
                  </td>
                  <td data-id="element-364" data-mp="2zk7rv1zo2mh0">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-365" data-mp="2zgjxy1qb7y1r">
                      <div className="pf" style={{
                    width: `${Math.round(p.closed / maxClosed * 100)}%`,
                    background: '#34d399'
                  }} data-id="element-366" data-mp="2uis77b1eihz5" />
                    </div>
                    <strong data-id="element-367" data-mp="2livff0jpt2zv">{p.closed}</strong>
                  </td>
                  <td style={{
                color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
                fontWeight: p.avgAge > 40 ? 600 : 400
              }} data-id="element-368" data-mp="2lkon8j62dt9g">
                    {p.avgAge}d
                  </td>
                  <td data-id="element-369" data-mp="jpe67sfxru30">{p.med}d</td>
                  <td data-id="element-370" data-mp="21pdlwnn5xgl9">{fmtG(p.gnarr)}</td>
                </tr>
                {expandedSC === p.n && <tr key={`exp-${p.n}`} data-id="element-371" data-mp="26nor2l2ij2x5">
                    <td colSpan={6} style={{
                padding: 0
              }} data-id="element-372" data-mp="26mndo9y0y4c9">
                      <div className="exp-inner" data-id="element-373" data-mp="2beoorsjghp0p">
                        <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#667085',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em'
                  }} data-id="element-374" data-mp="2bh1fov76z9pw">
                          In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)
                        </div>
                        <DealExpandedView deals={p.deals} mode={dealView} data-id="element-375" data-mp="1npw10jczmnh6" />
                        {p.closedDeals.length > 0 && <>
                            <div style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: '#059669',
                      margin: '12px 0 6px',
                      textTransform: 'uppercase',
                      letterSpacing: '.05em'
                    }} data-id="element-376" data-mp="1wqk62okeldhx">
                              Closed — {p.n} ({p.closed} deals)
                            </div>
                            <DealExpandedView deals={p.closedDeals} mode={dealView} data-id="element-377" data-mp="3870n2mblw26w" />
                          </>}
                      </div>
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-378" data-mp="3ob30xcafdci9" />
      <div className="tw" data-id="element-379" data-mp="2qnazg64c3p4v">
        <div className="sub" data-id="element-380" data-mp="2qo485cmusopn">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-381" data-mp="2vo8iekx64n7f">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-382" data-mp="30kz53r3hah1t">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-383" data-mp="35k22qqh4pkzr" />
          </table>
        </div>
      </div>
    </div>;
}